// Actions related to user interaction
import { batch } from "react-redux";
import {
  fetchSetLocation,
  setFocusElement,
  resetSampleSelection,
  calculateTotalSamplesNeeded,
  setMainViewSection,
  fetchSetDz,
  toggleAcquireBar,
  fetchPresetLocations,
  getLocationZandDzValues,
  saveCustomProtocol,
  toggleHeatmap,
  toggleEditMapMode,
  toggleAddNewLocationMode,
} from ".";
import {
  tempLocationId,
  tempLocationSymmetricalId,
  customFaceMapId,
  faceMapId,
  frontBodyMapId,
  backBodyMapId,
} from "../constants/constants";
import { getImageType } from "../constants/utilityFunctions";

export const RESTORE_LOCATION = "RESTORE_LOCATION";
export function restoreLocation(locationId) {
  return {
    type: RESTORE_LOCATION,
    locationId,
  };
}

export const REMOVE_LOCATION = "REMOVE_LOCATION";
export function removeLocation(locationId) {
  return {
    type: REMOVE_LOCATION,
    locationId,
  };
}

export const CHANGE_LOCATION_MAP_COORDINATES =
  "CHANGE_LOCATION_MAP_COORDINATES";
export function changeLocationMapCoordinates(locId, mapId, coords) {
  return {
    type: CHANGE_LOCATION_MAP_COORDINATES,
    locId,
    mapId,
    coords,
  };
}

export const SET_SELECTED_LOCATION_ID = "SET_SELECTED_LOCATION_ID";
export function setSelectedLocationId(locationId) {
  return {
    type: SET_SELECTED_LOCATION_ID,
    locationId: locationId,
  };
}

// Changes between LIST and BODY_MAP views
export const SET_LOCATION_VIEW_STYLE = "SET_LOCATION_VIEW_STYLE";
export function setLocationViewStyle(viewId) {
  return {
    type: SET_LOCATION_VIEW_STYLE,
    view: viewId,
  };
}

export const SET_LOCATION_NOTE = "SET_LOCATION_NOTE";
export function setLocationNote(locationId, note) {
  return {
    type: SET_LOCATION_NOTE,
    locationId,
    note,
  };
}

// Change body map image
export const SET_CURRENT_MAP_ID = "SET_CURRENT_MAP_ID";
export function setCurrentMapId(newBodyMapId) {
  return {
    type: SET_CURRENT_MAP_ID,
    id: newBodyMapId,
  };
}

export const SET_LOCATION_LIST = "SET_LOCATION_LIST";
export function setLocationList(newLoc) {
  return {
    type: SET_LOCATION_LIST,
    newLoc,
  };
}

export const SET_TEMP_LOCATION_INCLUDED_MAPS =
  "SET_TEMP_LOCATION_INCLUDED_MAPS";
export function setTempLocationIncludedMaps(locId, mapSet) {
  return {
    type: SET_TEMP_LOCATION_INCLUDED_MAPS,
    locId,
    mapSet,
  };
}

export const ADD_TEMP_LOCATION = "ADD_TEMP_LOCATION";
export function addTempLocation(symmetry = false) {
  return {
    type: ADD_TEMP_LOCATION,
    symmetry: symmetry,
  };
}

export const REMOVE_TEMP_LOCATION = "REMOVE_TEMP_LOCATION";
export function removeTempLocation(symmetry = false) {
  return {
    type: REMOVE_TEMP_LOCATION,
    symmetry: symmetry,
  };
}

export function selectLocation(locationId, acqType) {
  return (dispatch, getState) => {
    const state = getState();
    // if the requested location is not currently active
    // and the app isnt currently acquiring
    console.log("SELECTED!!");
    if (
      !state.device.acquiring &&
      locationId !== state.locations.selectedLocationId
    ) {
      dispatch(setSelectedLocationId(locationId)); // change active location view
      dispatch(setFocusElement(locationId)); //change focus element

      if (state.samples.selectedSampleId) {
        dispatch(resetSampleSelection()); // unselect all samples
      }

      // if connected to an external device
      if (state.device.connectionEstablished) {
        dispatch(fetchSetLocation(locationId, acqType)); // notify server of location change
        let locationRoi = getLocationZandDzValues(getState());
        dispatch(fetchSetDz(locationRoi.dz, locationRoi.z));
      }
      // if the location is not in the current location map
      const locationBodyMapIds = Object.keys(
        state.protocol.locations[locationId].maps
      );
      //skip activaBodyMap if the location has no body maps such as a new added location
      if (
        !locationBodyMapIds.includes(state.locations.currentMapId) &&
        locationBodyMapIds.length !== 0
      ) {
        dispatch(changeToActiveBodyMap(locationId)); // change the map to one that has it
      }
    }
    if (state.view.mainViewSection.orientation !== "LEFT") {
      dispatch(setMainViewSection("sampleFocused"));
    }
  };
}

export function showLocationInfoSection(locationId) {
  return (dispatch) => {
    dispatch(setMainViewSection("locationInfoFocused"));
    dispatch(resetSampleSelection());
    dispatch(setFocusElement(locationId));
  };
}

// change the active body map the first one that includes the selected location
export function changeToActiveBodyMap(locationId) {
  return (dispatch, getState) => {
    const state = getState();

    // array of all mapId for current location
    const locationMapIds = Object.keys(
      state.protocol.locations[locationId].maps
    );
    const allMapIds = Object.keys(state.protocol.maps);
    let mapId = state.locations.currentMapId;

    // get index of current mapId
    let index = allMapIds.indexOf(mapId);

    while (!locationMapIds.includes(mapId)) {
      if (index < allMapIds.length - 1) {
        index = index + 1;
        mapId = allMapIds[index];
      } else {
        index = 0;
        mapId = allMapIds[index];
      }
    }
    dispatch(setCurrentMapId(mapId)); // change bodyMap
  };
}

///////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////// ADD NEW LOCATION ///////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////

export function enterAddNewLocation(maps) {
  return (dispatch, getState) => {
    // Enter the "add new location" mode
    let state = getState();
    batch(() => {
      dispatch(toggleAcquireBar()); // hide acquire bar, prevent acquisitions
      if (state.utility.heatmap.status) {
        dispatch(toggleHeatmap()); // turn off heatmap
      }
      dispatch(setLocationViewStyle("BODY_MAP")); // switch to map view
      dispatch(toggleEditMapMode()); // turn on editing mode so the user can move the temporary location on the map
      dispatch(toggleAddNewLocationMode());
      dispatch(setMainViewSection("addNewLocationFocused")); // open add new location form
    });
    dispatch(fetchPresetLocations(maps)); // get list of preset locations that the user can select from
    dispatch(addTempLocation()); // add a temporary location for the user to edit
  };
}

export function exitAddNewLocation() {
  return (dispatch, getState) => {
    // Exit "add new location" mode
    let state = getState();

    if (state.utility.mode.createProtocolMode) {
      dispatch(
        saveCustomProtocol({
          protocolId: state.protocol.protocolId,
          protocolName: state.protocol.protocolName,
        })
      );
    } else {
      batch(() => {
        // acquire bar is hidden when adding new location, so if device connection is established bring back the acquire bar
        dispatch(toggleAcquireBar());
        dispatch(removeTempLocation()); // remove the temporary location
        dispatch(toggleEditMapMode()); // turn off edit mode
        dispatch(toggleAddNewLocationMode());
        dispatch(setLocationViewStyle("LIST")); // go back to LIST view
        dispatch(setMainViewSection("sampleFocused")); // close add new location dialog
      });

      if (state.locations.selectedLocationId === null) {
        // if no location is selected, then select one
        dispatch(
          selectLocation(
            state.locations.allIds[0],
            state.locations.currentAcqType
          )
        );
      }
    }
  };
}

export function addNewLocation(newLocationInfo, symmetryLocation) {
  return (dispatch, getState) => {
    let state = getState();
    let newLocations = {};
    // Add filename for each illustration
    if (newLocationInfo.instruction.illustration.len !== 0) {
      let imageType;
      newLocationInfo.instruction.illustration.forEach(
        (illustrationDict, index) => {
          imageType = getImageType(illustrationDict.image);
          illustrationDict.filename =
            state.protocol.protocolId +
            "_" +
            state.studyInfo.id +
            "_" +
            newLocationInfo.locationId +
            "_illustration_" +
            index +
            imageType;
        }
      );
    }

    if (!symmetryLocation) {
      newLocations[newLocationInfo.locationId] = {
        locationId: newLocationInfo.locationId,
        locationName: newLocationInfo.locationName,
        samplesNeeded: {},
        parameterId: newLocationInfo.parameterId,
        instruction: newLocationInfo.instruction,
        maps: {},
        new: true,
      };
      Object.keys(state.protocol.acq_types).forEach((acqType) => {
        newLocations[newLocationInfo.locationId].samplesNeeded[acqType] =
          Number(newLocationInfo[acqType].samplesNeeded);
      });
      Object.keys(state.protocol.locations[tempLocationId].maps).forEach(
        (mapId) => {
          if (newLocationInfo.maps[mapId]) {
            newLocations[newLocationInfo.locationId].maps[mapId] =
              state.protocol.locations[tempLocationId].maps[mapId];
          }
        }
      );
    } else {
      newLocations = {
        [newLocationInfo.locationId + "_RIGHT"]: {
          locationId: newLocationInfo.locationId + "_RIGHT",
          locationName: newLocationInfo.locationName + " (R)",
          samplesNeeded: {},
          parameterId: newLocationInfo.parameterId,
          instruction: newLocationInfo.instruction,
          maps: {},
          new: true,
        },
        [newLocationInfo.locationId + "_LEFT"]: {
          locationId: newLocationInfo.locationId + "_LEFT",
          locationName: newLocationInfo.locationName + " (L)",
          samplesNeeded: {},
          parameterId: newLocationInfo.parameterId,
          instruction: newLocationInfo.instruction,
          maps: {},
          new: true,
        },
      };

      Object.keys(state.protocol.acq_types).forEach((acqType) => {
        newLocations[newLocationInfo.locationId + "_RIGHT"].samplesNeeded[
          acqType
        ] = Number(newLocationInfo[acqType].samplesNeeded);
        newLocations[newLocationInfo.locationId + "_LEFT"].samplesNeeded[
          acqType
        ] = Number(newLocationInfo[acqType].samplesNeeded);
      });

      Object.keys(state.protocol.locations[tempLocationId].maps).forEach(
        (mapId) => {
          if (newLocationInfo.maps[mapId]) {
            if ([faceMapId, customFaceMapId, frontBodyMapId].includes(mapId)) {
              if (
                state.protocol.locations[tempLocationId].maps[mapId][1] < 50
              ) {
                newLocations[newLocationInfo.locationId + "_RIGHT"].maps[
                  mapId
                ] = state.protocol.locations[tempLocationId].maps[mapId];
                newLocations[newLocationInfo.locationId + "_LEFT"].maps[mapId] =
                  state.protocol.locations[tempLocationSymmetricalId].maps[
                    mapId
                  ];
              } else {
                newLocations[newLocationInfo.locationId + "_RIGHT"].maps[
                  mapId
                ] =
                  state.protocol.locations[tempLocationSymmetricalId].maps[
                    mapId
                  ];
                newLocations[newLocationInfo.locationId + "_LEFT"].maps[mapId] =
                  state.protocol.locations[tempLocationId].maps[mapId];
              }
            } else if (mapId === backBodyMapId) {
              if (
                state.protocol.locations[tempLocationId].maps[mapId][1] < 50
              ) {
                newLocations[newLocationInfo.locationId + "_RIGHT"].maps[
                  mapId
                ] =
                  state.protocol.locations[tempLocationSymmetricalId].maps[
                    mapId
                  ];
                newLocations[newLocationInfo.locationId + "_LEFT"].maps[mapId] =
                  state.protocol.locations[tempLocationId].maps[mapId];
              } else {
                newLocations[newLocationInfo.locationId + "_RIGHT"].maps[
                  mapId
                ] = state.protocol.locations[tempLocationId].maps[mapId];
                newLocations[newLocationInfo.locationId + "_LEFT"].maps[mapId] =
                  state.protocol.locations[tempLocationSymmetricalId].maps[
                    mapId
                  ];
              }
            }
          }
        }
      );
    }
    dispatch(removeTempLocation()); // remove blank location from location list
    dispatch(setNewLocation(newLocations)); // add loc to server, closeDialog tells whether or not to select new location/ close dialog
    dispatch(calculateTotalSamplesNeeded()); //update samples required and study progress
  };
}

export const SET_NEW_LOCATION = "SET_NEW_LOCATION";
export function setNewLocation(newLocations) {
  return {
    type: SET_NEW_LOCATION,
    newLocations,
  };
}

export const SET_CUSTOM_PROTOCOL_INFO = "SET_CUSTOM_PROTOCOL_INFO";
export function setCustomProtocolInfo(newProtocol) {
  return {
    type: SET_CUSTOM_PROTOCOL_INFO,
    newProtocol,
  };
}
